import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Review } from "@/types/review";
import ReviewMap from "@/components/reviews/ReviewMap";
import { toast } from "@/components/ui/use-toast";
import SingleReviewCard from "@/components/reviews/SingleReviewCard";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressHeader } from "@/components/reviews/detail/AddressHeader";

type SortOption = "newest" | "highest" | "lowest";

const AddressDetail = () => {
  const { addressId } = useParams(); // Fixed: changed from 'address' to 'addressId'
  const decodedAddress = addressId ? decodeURIComponent(addressId) : "";
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>("newest");

  // Transform database row to Review type with proper type casting
  const transformReview = (data: any): Review => {
    console.log("Transforming review data:", data);
    
    try {
      const transformed: Review = {
        id: data.id,
        user_id: data.user_id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        
        // Basic information
        community_name: data.community_name || undefined,
        landlord_name: data.landlord_name || undefined,
        address_detail: data.address_detail || "",
        address_full: data.address_full || undefined,
        display_address: data.display_address || undefined,
        country: data.country || "CA",
        region: data.region || "AB",
        coordinates: data.coordinates || { lat: 0, lng: 0 },
        full_coordinates: data.full_coordinates || undefined,
        display_coordinates: data.display_coordinates || undefined,
        
        // Privacy settings
        show_landlord_name: data.show_landlord_name !== false, // default to true if not specified
        hide_street_number: Boolean(data.hide_street_number),
        
        // Housing details with proper type casting
        housing_type: (data.housing_type as "basement" | "house" | "room" | "condo" | "apartment" | "semi-detached") || undefined,
        bedrooms: Number(data.bedrooms) || 0,
        living_rooms: Number(data.living_rooms) || 0,
        bathrooms: Number(data.bathrooms) || 0,
        
        // Rental period
        rental_year_start: data.rental_year_start || undefined,
        rental_year_end: data.rental_year_end || undefined,
        
        // Rating and comments
        rating: Number(data.rating) || 0,
        comment: data.comment || undefined,
        comment_translations: data.comment_translations || undefined,
        comment_en: data.comment_en || undefined,
        comment_zh_tw: data.comment_zh_tw || undefined,
        comment_zh_cn: data.comment_zh_cn || undefined,
        comment_language: data.comment_language || undefined,
        
        // Fees
        rent: String(data.rent) || "0",
        deposit_type: (data.deposit_type as "amount" | "months") || "amount",
        deposit_value: String(data.deposit_value) || "0",
        includes_utilities: Boolean(data.includes_utilities),
        utilities_included: Array.isArray(data.utilities_included) ? data.utilities_included : [],
        
        // Status
        rental_status: (data.rental_status as "viewed" | "renting" | "ended") || "viewed",
        
        // Special features
        immigrant_friendly: Boolean(data.immigrant_friendly),
        accepts_overseas_proof_of_funds: Boolean(data.accepts_overseas_proof_of_funds),
        accepts_overseas_employment_proof: Boolean(data.accepts_overseas_employment_proof),
        
        // Pet friendly
        pet_allowed: Boolean(data.pet_allowed),
        pet_fee: Boolean(data.pet_fee),
        no_pet_limit_count: Boolean(data.no_pet_limit_count),
        no_pet_limit_type: Boolean(data.no_pet_limit_type),
        
        // Application requirements
        requires_employment_proof: Boolean(data.requires_employment_proof),
        requires_credit_check: Boolean(data.requires_credit_check),
        has_income_requirement: Boolean(data.has_income_requirement),
        requires_financial_proof: Boolean(data.requires_financial_proof),
        no_proof_required: Boolean(data.no_proof_required),
        
        // Contract flexibility
        allows_daily_rental: Boolean(data.allows_daily_rental),
        allows_monthly_rental: Boolean(data.allows_monthly_rental),
        allows_semiannual_rental: Boolean(data.allows_semiannual_rental),
        allows_contract_termination: Boolean(data.allows_contract_termination),
        allows_contract_transfer: Boolean(data.allows_contract_transfer),
        
        // Other features
        min_rental_period: data.min_rental_period || null,
        notice_period: data.notice_period || null,
        viewing_during_lease: Boolean(data.viewing_during_lease),
        required_documents: Array.isArray(data.required_documents) ? data.required_documents : [],
        
        // Images and parking
        image_urls: Array.isArray(data.image_urls) ? data.image_urls : [],
        parking_information: Array.isArray(data.parking_information) ? data.parking_information : []
      };
      
      console.log("Successfully transformed review:", transformed);
      return transformed;
    } catch (error) {
      console.error("Error transforming review data:", error, data);
      throw error;
    }
  };

  useEffect(() => {
    const fetchAddressReviews = async () => {
      console.log("AddressDetail: Starting to fetch reviews");
      
      if (!decodedAddress) {
        console.log("AddressDetail: No address provided");
        setLoading(false);
        setError("沒有提供地址");
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        console.log("AddressDetail: Fetching reviews for address:", decodedAddress);
        
        // Query both address_detail and display_address fields to handle privacy settings  
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .or(`address_detail.ilike."%${decodedAddress}%",display_address.ilike."%${decodedAddress}%"`);
          
        console.log("AddressDetail: Database response:", { data, error });
          
        if (error) {
          console.error("AddressDetail: Database error:", error);
          throw error;
        }
        
        if (!data) {
          console.log("AddressDetail: No data returned from database");
          setReviews([]);
          return;
        }
        
        console.log("AddressDetail: Raw reviews data:", data);
        console.log("AddressDetail: Number of reviews found:", data.length);
        
        if (data.length === 0) {
          console.log("AddressDetail: No reviews found for this address");
          setReviews([]);
          return;
        }
        
        const transformedReviews = data.map(transformReview);
        console.log("AddressDetail: Transformed reviews:", transformedReviews);
        setReviews(transformedReviews);
      } catch (error: any) {
        console.error("AddressDetail: Error fetching address reviews:", error);
        setError(error.message || "載入評價時發生錯誤");
        toast({
          variant: "destructive",
          title: t('errorFetchingReviews'),
          description: error.message
        });
        setReviews([]);
      } finally {
        setLoading(false);
        console.log("AddressDetail: Fetch operation completed");
      }
    };
    
    fetchAddressReviews();
  }, [decodedAddress, t]);

  const sortedReviews = useMemo(() => {
    if (!reviews.length) return [];
    const reviewsCopy = [...reviews];
    switch (sortOption) {
      case "newest":
        return reviewsCopy.sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
      case "highest":
        return reviewsCopy.sort((a, b) => b.rating - a.rating);
      case "lowest":
        return reviewsCopy.sort((a, b) => a.rating - b.rating);
      default:
        return reviewsCopy;
    }
  }, [reviews, sortOption]);

  const averageRating = useMemo(() => {
    if (!reviews.length) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return sum / reviews.length;
  }, [reviews]);

  // Get the earliest and latest rental years from the reviews
  const getRentalYearsRange = () => {
    const reviewsWithYears = reviews.filter(r => r.rental_year_start);
    if (reviewsWithYears.length === 0) return null;
    
    const minYear = Math.min(...reviewsWithYears.map(r => r.rental_year_start as number));
    
    // Check if any reviews have end years
    const reviewsWithEndYears = reviewsWithYears.filter(r => r.rental_year_end);
    
    if (reviewsWithEndYears.length === 0) {
      return `${minYear}`;
    }
    
    const maxYear = Math.max(...reviewsWithEndYears.map(r => r.rental_year_end as number));
    return minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`;
  };
  
  // Get display title and address (respecting privacy settings)
  const getDisplayTitle = () => {
    if (!reviews.length) return decodedAddress;
    
    // Try to find a community name first
    const communityName = reviews.find(r => r.community_name)?.community_name;
    if (communityName) return communityName;
    
    // Then try to find a landlord name (respecting privacy settings)
    const reviewWithLandlord = reviews.find(r => r.landlord_name && r.show_landlord_name !== false);
    if (reviewWithLandlord?.landlord_name) return reviewWithLandlord.landlord_name;
    
    // Fall back to display address (respecting privacy settings)
    const displayAddress = getDisplayAddress();
    return displayAddress || decodedAddress;
  };

  // Get display address (respecting privacy settings)
  const getDisplayAddress = () => {
    if (!reviews.length) return decodedAddress;
    
    // Use display_address from reviews if available and hide_street_number is true
    const reviewWithDisplayAddress = reviews.find(r => r.display_address && r.hide_street_number);
    if (reviewWithDisplayAddress?.display_address) {
      return reviewWithDisplayAddress.display_address;
    }
    
    // Fall back to original address
    return decodedAddress;
  };

  console.log("AddressDetail: Rendering component", { 
    loading, 
    error, 
    reviewsCount: reviews.length, 
    decodedAddress 
  });

  if (loading) {
    console.log("AddressDetail: Rendering loading state");
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="flex flex-col gap-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-96" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    console.log("AddressDetail: Rendering error state:", error);
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/reviews" className="flex items-center gap-2">
                <ChevronLeft className="h-4 w-4" />
                {t('backToReviews')}
              </Link>
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <h1 className="text-2xl font-bold">載入錯誤</h1>
            <p className="text-muted-foreground">
              {error}
            </p>
            <Button onClick={() => window.location.reload()}>
              重新載入
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!reviews.length) {
    console.log("AddressDetail: Rendering no reviews state");
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/reviews" className="flex items-center gap-2">
                <ChevronLeft className="h-4 w-4" />
                {t('backToReviews')}
              </Link>
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <h1 className="text-2xl font-bold">{t('reviews.noReviewsFound')}</h1>
            <p className="text-muted-foreground">
              {t('noReviewsForAddress')}: {decodedAddress}
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const rentalYearsRange = getRentalYearsRange();
  const displayTitle = getDisplayTitle();
  const displayAddress = getDisplayAddress();

  console.log("AddressDetail: Rendering success state with", { 
    reviews: reviews.length, 
    sortedReviews: sortedReviews.length, 
    displayTitle,
    averageRating 
  });

  return (
    <MainLayout>
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/reviews" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {t('backToReviews')}
            </Link>
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <AddressHeader 
              address={displayAddress} 
              averageRating={averageRating} 
              totalReviews={reviews.length} 
              rentalYearsRange={rentalYearsRange} 
              displayTitle={displayTitle}
            />
            
            <div className="h-60 border border-border rounded-lg overflow-hidden shadow-sm">
              <ReviewMap reviews={reviews} height="100%" clickable={false} />
            </div>
            
            <Separator className="my-6" />
            
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {language === 'zh-TW' ? '地址評價' : t('addressReviews')}
              </h2>
              <div className="w-48">
                <Select value={sortOption} onValueChange={value => setSortOption(value as SortOption)}>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'zh-TW' ? '排序方式' : t('sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="newest">{language === 'zh-TW' ? '最新到最舊' : t('sortNewest')}</SelectItem>
                      <SelectItem value="highest">{language === 'zh-TW' ? '評分高到低' : t('sortHighest')}</SelectItem>
                      <SelectItem value="lowest">{language === 'zh-TW' ? '評分低到高' : t('sortLowest')}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-4">
              {sortedReviews.map(review => (
                <SingleReviewCard key={review.id} review={review} />
              ))}
            </div>
          </div>
          
          <div className="lg:col-span-1">
            {/* Sidebar content removed as it's integrated above the map */}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddressDetail;
